import React, { useEffect, useState } from "react";
import { Button } from "@/components/bnp-ui";

import { InvestmentCard, TawkChat } from "@/components/shared";

import {
  BuildingHome,
  ContactHome,
  InvestmentHome,
  LogoGreen,
  PaperClipHome,
} from "@/components/bnp-icons";

import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import Link from "next/link";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useQuery } from "@tanstack/react-query";
import { InvestmentService, PropertyService, RealtorService } from "@/services";
import Image from "next/image";
import { NoSubscriptions } from "@/components/bnp-illustrations";
import TestimonialSlideshow from "./testimonials";
import FeaturedMedia from "./FeaturedMedia";
import BlogPost from "./blog";
import { motion } from "framer-motion";
import useAuthToken from "@/hooks/useAuthToken";
import { AdPage, AdPlacement, type Advertisement } from "@/types/advertising";
import { getAddress, getUserLocation } from "@/utils/location";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { usePageAds } from "@/hooks/usePageAds";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import parseHttpError from "@/utils/parseHttpError";
import HomeHeroFilter from "./filter/HomeFilter";
import { Property } from "@/types/property";
import { HomepagePropertiesQuery } from "@/types";
import UrlOptimizedListingCardContainer from "@/components/seo/UrlOptimizedListingCardContainer";

interface PageAdsData {
  bannerAds: Advertisement[];
  sidebarAds: Advertisement[];
  inFeedAds: Advertisement[];
  headerAds: Advertisement[];
  footerAds: Advertisement[];
  isLoading: boolean;
  error: unknown;
}

interface HomeProps {
  pageAds?: PageAdsData;
}

const Home: React.FC<HomeProps> = () => {
  const pageAds = usePageAds(AdPage.HOME);
  const footerAds = pageAds?.footerAds || [];
  const headerAds = pageAds?.headerAds || [];
  const infeedAds = pageAds?.inFeedAds || [];
  const sidebarAds = pageAds?.sidebarAds || [];
  const popupAds = pageAds?.popupAds || [];

  const runtimeEnvironment = getRuntimeEnvironment();

  const [properties, setProperties] = useState([]);
  const [managedProperties, setManagedProperties] = useState<Property[]>([]);
  const [project, setProject] = useState<any>(null);
  const router = useRouter();
  const { projectId } = router.query;
  const [minHeight, setMinHeight] = useState("250vh");
  const [investments, setInvestments] = useState<any>([]);
  const { user, token } = useAuthToken();

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      setMinHeight(isMobile ? "100vh" : "250vh");
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPropertiesRequest = async () => {
    // 1. Initialize variables we need for the request payload
    let lat: number | null = null;
    let lng: number | null = null;
    let address: string | undefined = undefined;

    try {
      // Attempt to get coordinates from the device browser
      const coords = await getUserLocation();
      lat = coords.lat;
      lng = coords.lng;

      // Only query reverse geocoding if coordinates succeeded
      address = await getAddress(lat, lng);
    } catch (locationError) {
      // 2. Catch permission errors silently and log for visibility
      console.warn(
        "[Properties] Location setup omitted or denied. Falling back to non-geo layout defaults.",
        locationError,
      );
      // Lat, lng, and address will remain null/undefined, which triggers the fallback behavior
    }

    try {
      // 3. Build the payload dynamically based on whether location was captured
      const payload = {
        page: 1,
        pageSize: 6,
        ...(address && { userCurrentLocation: address }),
        ...(lat && { latitude: lat.toString() }),
        ...(lng && { longitude: lng.toString() }),
      };

      const response = await PropertyService.GetPropertiesWithGeo(payload);
      console.log("Property API response:", response);
      return response?.data;
    } catch (error: any) {
      // Catch actual API or network execution breakdowns
      console.error("[Properties] Fetch API error:", error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: propertiesData,
    isLoading: propertiesIsLoading,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
    isSuccess: propertiesIsSuccess,
  } = useQuery({
    queryKey: ["properties"],
    queryFn: getPropertiesRequest,
    staleTime: 60 * 60 * 1000,
  });

  const getManagedPropertiesRequest = async () => {
    const payload: HomepagePropertiesQuery = {
      page: 1,
      pageSize: 6,
    };
    const response = await PropertyService.GetManagedProperties(payload);
    return response?.data;
  };

  const { data: managedPropertiesData, isLoading: managedPropertiesIsLoading } =
    useQuery({
      queryKey: ["managed-properties"],
      queryFn: getManagedPropertiesRequest,
      staleTime: 60 * 60 * 1000,
    });

  useEffect(() => {
    if (managedPropertiesData?.data) {
      setManagedProperties(managedPropertiesData.data);
    }
  }, [managedPropertiesData]);

  useEffect(() => {
    console.log(propertiesData, projectId, project);
    if (propertiesData && !projectId && !project) {
      console.log("goodiluck");
      setProperties(propertiesData?.data);
    }
  }, [propertiesData, projectId, project]);

  const getProjectRequest = async () => {
    try {
      // console.log(getValues());
      // const response = await ProjectService.GetProjectById(projectId || "");
      // return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: projectData,
    isLoading: projectIsLoading,
    isError,
    refetch,
    isSuccess,
  } = useQuery({
    queryKey: [`project-${projectId}`],
    queryFn: getProjectRequest,
    staleTime: 60 * 60 * 1000,
    enabled: !!projectId,
  });

  useEffect(() => {
    if (projectData) {
      // console.log(projectData?.data?.matchingListings);
      //setProject(projectData?.data?.project);
      //setProperties(projectData?.data?.matchingListings);
    }
  }, [projectData]);

  const getInvestments = async () => {
    try {
      // console.log(getValues());
      const response = await InvestmentService.GetInvestmentOfferings();
      console.log(response?.data);
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: investmentsData,
    isLoading: investmentsIsLoading,
    isError: investmentsIsError,
    refetch: investmentsRefetch,
    isSuccess: investmentsIsSuccess,
  } = useQuery({
    queryKey: ["investments"],
    queryFn: getInvestments,
    staleTime: 0,
  });

  useEffect(() => {
    if (!investmentsData || !investmentsData.data) return;
    if (investmentsData) {
      setInvestments(investmentsData?.data);
    }
    const developmentData = investmentsData.data.map((development: any) => {
      let updated = { ...development };

      if (development.developmentType === "off-plan-sale") {
        updated.totalInvestmentAmount =
          development.unitsAvailableForSubscription * development.amountPerUnit;
      }

      return updated;
    });

    setInvestments(developmentData);
  }, [investmentsData]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };
  const fadeInUpWithHover = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.3,
      },
    },
  };

  const scaleIn = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const boxVariants = {
    hidden: {
      x: 100,
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  //      headerSlot={headerAds[0] ? <HeaderAd ad={headerAds[0]} /> : undefined}

  return (
    <>
      <HomeWrapper>
        <div className={containerWidth}>
          <div className="w-full max-w-6xl mx-auto !bg-white p-4 mt-[-40px] relative rounded-[40px]">
            <HomeHeroFilter />
          </div>

          {headerAds[0] && (
            <div className="w-full max-w-[1280px] mx-auto px-4 pt-4 pb-4">
              <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
            </div>
          )}

          <div className="flex flex-col items-center w-full">
            <div className="flex flex-col items-center w-full bg-[radial-gradient(circle_at_center,_#E4F9FD_0%,_#ffffff_70%)]">
              <div className="flex flex-col lg:flex-row items-center lg:items-start w-full lg:w-[1004px] lg:justify-between gap-6 mt-16 px-4">
                <div className="flex flex-col gap-4 items-center lg:items-start lg:w-[713px] text-center lg:text-left">
                  <h1 className="text-main-900 font-bold text-[24px] lg:text-[42px]">
                    Explore more than 2,000 choice properties, tailored to your location
                  </h1>
                  <p className="text-main-900 font-normal text-base lg:w-[399px]">
                    We use your location to bring you the best homes. Some of
                    our top-picked properties ready for you to explore.
                  </p>
                </div>
                <Link
                  href={"/properties"}
                  className="w-full sm:w-auto lg:w-[243px] flex justify-center"
                >
                  <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold w-full sm:w-[275px] h-[52px] rounded-lg">
                    Discover properties
                  </Button>
                </Link>
              </div>

              <div className="flex justify-center items-center mt-[16px] w-full lg:w-[1104px] mb-20 p-2 py-4 px-4">
                {propertiesIsLoading ? (
                  <div className="flex flex-col justify-center items-center w-full h-fit">
                    <div className="w-fit h-fit flex justify-center">
                      <Image
                        //@ts-ignore
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"property"}
                        className="w-[400px] h-[400px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                ) : properties?.length > 0 ? (
                  <div className="w-full flex flex-col items-center">
                    {/* Added justify-items-center to force mobile grid cards to snap directly center */}
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerChildren}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-4 mt-8 w-full"
                    >
                      {properties
                        ?.slice(0, 3)
                        .map((property: Property, index: number) => (
                          <motion.div
                            key={index}
                            variants={fadeInUpWithHover}
                            custom={index}
                            className="w-full flex justify-center"
                          >
                            <UrlOptimizedListingCardContainer
                              key={index}
                              property={property}
                            />
                          </motion.div>
                        ))}
                    </motion.div>
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center">
                    {project && (
                      <h1 className="text-[24px] lg:text-[32px] text-center">
                        Matching properties for &quot;{project?.name}&quot;
                      </h1>
                    )}
                    <div className="w-full justify-center items-center flex flex-col gap-[38px] mt-[60px] lg:mt-[95px] text-center px-4">
                      <NoSubscriptions />
                      <p className="text-grey-7 lg:w-[408px] text-lg lg:text-xl">
                        {project
                          ? "No properties found for this project"
                          : "No properties found"}
                      </p>

                      <Button
                        className="bg-bnp-dark text-white text-sm font-medium w-[157px] h-[45px] rounded-lg"
                        onClick={() => {
                          setProject(null);
                          router.replace("/properties", undefined, {
                            shallow: true,
                          });
                          propertiesRefetch();
                        }}
                      >
                        Explore properties
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-center w-full bg-gradient-to-b from-white to-[#E4F9FD]">
              <div className="flex flex-col lg:flex-row items-center lg:items-start w-full lg:w-[1104px] lg:justify-between gap-6 mt-16 px-4">
                <div className="flex flex-col gap-4 items-center lg:items-start  text-center lg:text-left">
                  <h1 className="text-main-900 font-bold text-[24px] lg:text-[42px]">
                    Explore Our Managed Properties
                  </h1>
                  <p className="text-main-900 font-normal text-base lg:w-[399px]">
                    Managed properties are homes we directly control, so you
                    deal with us from start to finish - no middlemen, just one
                    trusted point of contact.
                  </p>
                </div>
                <Link
                  href={"/managed-properties"}
                  className="w-full sm:w-auto lg:w-[243px] flex justify-center"
                >
                  <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold w-full sm:w-[275px] h-[52px] rounded-lg">
                    Discover managed properties
                  </Button>
                </Link>
              </div>

              <div className="flex justify-center items-center mt-[16px] w-full lg:w-[1104px] mb-20 p-2 py-4 px-4">
                {managedPropertiesIsLoading ? (
                  <div className="flex flex-col justify-center items-center w-full h-fit">
                    <div className="w-fit h-fit flex justify-center">
                      <Image
                        //@ts-ignore
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"property"}
                        className="w-[400px] h-[400px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                ) : managedProperties?.length > 0 ? (
                  <div className="w-full flex flex-col items-center">
                    {/* Added justify-items-center to force mobile grid cards to snap directly center */}
                    <motion.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={staggerChildren}
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-4 mt-8 w-full"
                    >
                      {managedProperties
                        ?.slice(0, 3)
                        .map((property: Property, index: number) => (
                          <motion.div
                            key={index}
                            variants={fadeInUpWithHover}
                            custom={index}
                            className="w-full flex justify-center"
                          >
                            <UrlOptimizedListingCardContainer
                              key={index}
                              property={property}
                            />
                          </motion.div>
                        ))}
                    </motion.div>
                    {runtimeEnvironment.isLocal && (
                      <div className="w-full hidden flex-col items-center">
                        <div className="flex flex-col lg:flex-row items-center lg:items-start w-full lg:w-[1014px] lg:justify-between gap-6 mt-16 px-4">
                          <div className="flex flex-col gap-4 w-full lg:w-[793px] text-center lg:text-left">
                            <h1 className="text-main-900 font-bold text-[24px] lg:text-[42px]">
                              Unlock Managed Development Offerings
                            </h1>
                            <p className="text-main-900 font-normal text-base w-full">
                              Secure your financial future - the real estate
                              way!
                            </p>
                          </div>
                          <Link
                            href={
                              user && token
                                ? "/user/dashboard?tab=active-development-offerings"
                                : "/managed-developments"
                            }
                            className="w-full sm:w-auto lg:w-[243px] flex justify-center"
                          >
                            <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold w-full lg:w-[243px] h-[52px] rounded-lg">
                              See Offerings
                            </Button>
                          </Link>
                        </div>

                        <motion.div
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          variants={staggerChildren}
                          className="w-full flex justify-center gap-3 pb-10 flex-wrap mt-12"
                        >
                          {investments.length > 0 ? (
                            <>
                              {investments
                                .slice(0, 3)
                                .map((investment: any, index: number) => {
                                  return (
                                    <motion.div
                                      key={index}
                                      variants={fadeInUpWithHover}
                                      className="w-full sm:w-auto flex justify-center h-fit"
                                    >
                                      <Link
                                        href={`/investment/${investment?.id}`}
                                        className="w-full sm:w-fit flex justify-center h-fit"
                                      >
                                        <InvestmentCard
                                          title={investment?.description}
                                          amountRequired={
                                            investment?.totalInvestmentAmount
                                          }
                                          propertyStats={{
                                            amountRaised:
                                              investment?.amountAlreadyRaised,
                                            subscriberCount:
                                              investment?.subscriberCount,
                                            expectedReturns:
                                              investment?.expectedReturns,
                                            fundedDate: investment?.fundedDate,
                                            minimumBuyIn:
                                              investment?.minimumInvestment,
                                            preferredListingEndDate:
                                              investment?.preferredListingEndDate,
                                            deliveryDate:
                                              investment?.deliveryDate,
                                            targetRedemptionDate:
                                              investment?.targetRedemptionDate,
                                            developmentType:
                                              investment?.developmentType,
                                            image_url: investment?.image_url,
                                            amountPerUnit:
                                              investment?.amountPerUnit,
                                            subsequentMonthlyDeposit:
                                              investment?.subsequentMonthlyDeposit,
                                            initialDeposit:
                                              investment?.initialDeposit,
                                          }}
                                          currencyPrefix="₦"
                                        />
                                      </Link>
                                    </motion.div>
                                  );
                                })}
                            </>
                          ) : (
                            <div className="w-full justify-center items-center flex flex-col text-center">
                              <NoSubscriptions />
                              <p className="text-grey-7 lg:w-[408px] mt-4 text-lg">
                                No active investments yet
                              </p>
                            </div>
                          )}
                        </motion.div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center">
                    <div className="w-full justify-center items-center flex flex-col gap-[38px] mt-[60px] lg:mt-[95px] text-center px-4">
                      <NoSubscriptions />
                      <p className="text-grey-7 lg:w-[408px] text-lg lg:text-xl">
                        No managed properties found
                      </p>

                      <Button
                        className="bg-bnp-dark text-white text-sm font-medium w-[157px] h-[45px] rounded-lg"
                        onClick={() => router.push("/managed-properties")}
                      >
                        Explore managed properties
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div
              className="hidden flex flex-col lg:flex-row gap-6 mt-8 lg:my-16
        "
            >
              <div className="flex flex-col gap-6 lg:w-[442px]">
                <h1 className="text-main-900 text-center lg:text-left font-bold text-base lg:text-xl">
                  Meet our developers and realtors
                </h1>
                <p className="text-main-900 text-center lg:text-left font-normal text-base">
                  Some of our experts with local market knowledge, personalized
                  service, and a commitment to finding your perfect home
                </p>
                <Link href={"/realtors"} className="w-[100%]">
                  <Button className="mt-4 bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
                    Search companies
                  </Button>
                </Link>
                {/* <Link
            href={
              user && token
                ? "/user/dashboard/profile"
                : "/auth/sign-up"
            }
            className="w-[100%]"
          >
            <Button className=" bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
              Become a realtor
            </Button>
          </Link> */}
              </div>
              <div>
                {" "}
                <Image
                  //@ts-ignore
                  src={"/assets/images/realtor.avif"}
                  width={200}
                  height={300}
                  alt={"property"}
                  className="w-[100%] lg:w-[560px] lg:h-[300px]"
                />
                {/* <Link href={"/realtors"} className="w-[100%]">
            <Button className="mt-4 bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
              Find partner realtors
            </Button>
          </Link> */}
              </div>
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
              className="px-4 flex items-center  w-[100%] lg:w-[1104px] flex-col gap-6 mt-16"
            >
              <motion.div
                variants={fadeInUp}
                className="flex flex-col items-center gap-4 w-[100%] lg:w-[713px]"
              >
                <h1 className="text-main-900 text-center font-bold text-[24px] -tracking-[0.5] lg:text-[42px]">
                  Your gateway to exclusive real estate opportunities
                </h1>
                <p className="text-main-900 text-center font-normal text-base">
                  Subscribe to portions of high-value properties that were
                  previously out of reach, allowing you to diversify your
                  portfolio across multiple locations and property types.
                </p>
              </motion.div>
            </motion.div>

            <div
              className="px-4 flex flex-col lg:flex-row gap-6 mt-8 lg:my-16
        "
            >
              <div className="flex flex-col gap-6 lg:w-[442px]">
                <h1 className="text-main-900 text-center lg:text-left font-bold text-base lg:text-xl">
                  Meet our developers and realtors
                </h1>
                <p className="text-main-900 text-center lg:text-left font-normal text-base">
                  Some of our experts with local market knowledge, personalized
                  service, and a commitment to finding your perfect home
                </p>
                <Link href={"/realtors"} className="w-[100%]">
                  <Button className="mt-4 bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
                    Search companies
                  </Button>
                </Link>
                {/* <Link
            href={
              user && token
                ? "/user/dashboard/profile"
                : "/auth/sign-up"
            }
            className="w-[100%]"
          >
            <Button className=" bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
              Become a realtor
            </Button>
          </Link> */}
              </div>
              <div>
                {" "}
                <Image
                  //@ts-ignore
                  src={"/assets/images/realtor.avif"}
                  width={200}
                  height={300}
                  alt={"property"}
                  className="w-[100%] lg:w-[560px] lg:h-[300px]"
                />
                {/* <Link href={"/realtors"} className="w-[100%]">
            <Button className="mt-4 bg-bnp-cyan font-sans text-white text-sm font-semibold w-[100%] md:w-[213px] h-[52px] rounded-lg">
              Find partner realtors
            </Button>
          </Link> */}
              </div>
            </div>

            <div className="hidden lg:flex relative w-[327px] lg:w-[941px] h-[494px] bg-[url('/assets/images/onboarding-photo.png')] mt-24 lg:mt-12 bg-cover bg-no-repeat rounded-[24px]">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
                className="absolute right-[192px] lg:left-[-120px] top-[-42px] lg:top-[74px] flex flex-col justify-center
    lg:w-[495px] lg:text-[32px] font-bold p-8 rounded-[12px] h-[294px] bg-bnp-dark bg-bottom bg-[url('/assets/images/hero-bg.png')] bg-contain bg-no-repeat text-green-secondary"
              >
                <motion.div variants={fadeInUp}>
                  <LogoGreen />
                  <p>Nigeria&apos;s Premier Real Estate Marketplace</p>
                  <Link
                    href={user && token ? "/user/dashboard" : "/auth/sign-up"}
                  >
                    <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold mt-8 w-[176px] h-[58px] rounded-lg">
                      Join Us Today
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
                className="flex flex-col gap-4 absolute right-[-16px] lg:left-[564px] lg:top-[54px] top-[262px]"
              >
                <motion.div
                  variants={boxVariants}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center p-4 gap-6 bg-white ml-14 lg:w-[434px] lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg"
                >
                  <div>
                    <BuildingHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark font-bold lg:text-[20px]">
                      Buy/Sell a property with ease
                    </h3>
                    <p className="text-bnp-dark font-normal text-base">
                      Real estate made easy
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  variants={boxVariants}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center p-4 gap-6 bg-white ml-8 lg:w-[500px] lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg"
                >
                  <div>
                    <InvestmentHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark font-bold lg:text-[20px]">
                      Curated Development Opportunities
                    </h3>
                    <p className="text-bnp-dark font-normal text-base">
                      Subscribe to portions of properties with plans
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  variants={boxVariants}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center p-4 gap-6 bg-white lg:w-[489px] lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg"
                >
                  <div>
                    <ContactHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark font-bold lg:text-[20px]">
                      Contact realtors directly
                    </h3>
                    <p className="text-bnp-dark font-normal text-base">
                      Get access to our growing network of realtors
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            <div className="lg:hidden px-4 relative w-[327px] lg:w-[941px] h-[494px] bg-[url('/assets/images/onboarding-photo.png')] mt-24 lg:mt-12  bg-cover bg-no-repeat rounded-[24px]">
              <div
                className="absolute right-[92px] lg:left-[-120px] top-[-42px] lg:top-[74px] flex flex-col justify-center
        lg:w-[495px] lg:text-[32px] font-bold p-8 rounded-[12px] h-[294px] bg-bnp-dark bg-bottom  bg-[url('/assets/images/hero-bg.png')] bg-contain bg-no-repeat text-green-secondary "
              >
                <LogoGreen />
                <p>Nigeria&apos;s Premier Real Estate Marketplace</p>
                <Link
                  href={user && token ? "/user/dashboard" : "/auth/sign-up"}
                >
                  <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold mt-8 w-[176px] h-[58px] rounded-lg">
                    Join Us Today
                  </Button>
                </Link>
              </div>

              <div className="flex flex-col gap-4 absolute right-[-6px] lg:left-[564px] lg:top-[54px] top-[262px]">
                <div className="flex items-center p-4 gap-3 bg-white ml-14 lg:w-[434px]  lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg">
                  <div>
                    <BuildingHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark  font-bold lg:text-[20px]">
                      Buy/Sell a property with ease
                    </h3>
                    <p className="text-bnp-dark  font-normal text-[13px]">
                      Real estate made easy
                    </p>
                  </div>
                </div>

                <div className="flex items-center p-4 gap-3 bg-white ml-8 lg:w-[457px] lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg">
                  <div>
                    <InvestmentHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark  font-bold lg:text-[20px]">
                      Curated Development Opportunities
                    </h3>
                    <p className="text-bnp-dark  font-normal text-[13px]">
                      Subscribe to portions of properties with plans
                    </p>
                  </div>
                </div>

                <div className="flex items-center p-4 gap-3 bg-white lg:w-[489px] lg:h-[112px] border-[1.5px] border-soft-200 rounded-lg">
                  <div>
                    <ContactHome />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-bnp-dark  font-bold lg:text-[20px]">
                      Contact realtors directly
                    </h3>
                    <p className="text-bnp-dark  font-normal text-[13px]">
                      Get access to our growing network of realtors
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 flex flex-col lg:flex-row gap-4 mt-64 lg:mt-16">
              <div className="flex flex-col gap-4">
                <div className="bg-[#F5F7F9] p-6 rounded-[8px] w-[100%] lg:w-[509px] lg:h-[216px] flex gap-8">
                  <div>
                    <PaperClipHome />
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-bnp-dark  font-bold lg:text-[20px]">
                      Curated Development Opportunities
                    </h3>
                    <p className="text-bnp-dark  font-normal text-base">
                      You&apos;ll have access to a diverse range of pre-vetted
                      properties, from residential rentals to commercial real
                      estate, ensuring your portfolio aligns with your financial
                      objectives.
                    </p>
                  </div>
                </div>

                <div className="bg-[#F5F7F9] p-6 rounded-[8px] w-[100%] lg:w-[509px] lg:h-[216px] flex gap-8">
                  <div>
                    <PaperClipHome />
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-bnp-dark  font-bold text-[20px]">
                      Real-Time Analytics
                    </h3>
                    <p className="text-bnp-dark  font-normal text-base">
                      Stay informed about your investments with our
                      comprehensive, user-friendly dashboard. Track property
                      performance, occupancy rates, and cash flow in real-time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="bg-[#F5F7F9] p-6 rounded-[8px] w-[100%] lg:w-[509px] lg:h-[242px] flex gap-8">
                  <div>
                    <PaperClipHome />
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-bnp-dark  font-bold text-[20px]">
                      Expert Support
                    </h3>
                    <p className="text-bnp-dark  font-normal text-base">
                      Whether you&apos;re a first-time investor or a seasoned
                      pro, our support team is available to answer your
                      questions, offer insights, and help you navigate the
                      complexities of real estate investing with confidence.
                    </p>
                  </div>
                </div>

                <div className="bg-[#F5F7F9] p-6 rounded-[8px] w-[100%] lg:w-[509px] lg:h-[216px] flex gap-8">
                  <div>
                    <PaperClipHome />
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-bnp-dark  font-bold text-[20px]">
                      Fractional Ownership
                    </h3>
                    <p className="text-bnp-dark  font-normal text-base">
                      Break down barriers to real estate investing with our
                      fractional ownership model, this approach minimizes risk
                      while maximizing your potential for returns.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* =========================================== IN FEED AD =================================== */}
            {infeedAds[0] && (
              <AdRenderer ad={infeedAds[0]} placement={AdPlacement.IN_FEED} />
            )}
            {popupAds[0] && (
              <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
            )}
            {/* =========================================== IN FEED AD =================================== */}
            {
              (
                <FeaturedMedia />
              )
            }
            {
              !runtimeEnvironment.isProduction && (
                <TestimonialSlideshow />
              )
            }
            <BlogPost />
          </div>

          <div className="px-4">

            <div className="w-full px-6 py-12 bg-bnp-dark mt-[85px] rounded-lg text-white flex justify-center items-center mb-12">
              <div className="w-full max-w-[657px] flex flex-col items-center text-center">

                {/* Heading: Smaller & bold on mobile, larger on desktop */}
                <h1 className="font-bold text-2xl sm:text-3xl lg:text-[34px] lg:font-semibold leading-tight">
                  Never Miss Your Ideal Property
                </h1>

                {/* Body text: Optimized responsive text size & line height */}
                <p className="text-sm sm:text-base font-normal sm:font-medium mt-3 text-white/90 max-w-[540px]">
                  Stop starting your search from scratch. Set your property preferences once
                  and get real-time alerts the moment matching listings hit the market.
                </p>

                {/* Primary CTA */}
                <Link
                  href={
                    user && token
                      ? "/user/dashboard/properties/my-projects"
                      : "/auth/sign-up"
                  }
                  className="w-full sm:w-auto"
                >
                  <Button className="bg-bnp-cyan font-sans text-white text-sm font-semibold mt-6 sm:mt-8 w-full sm:w-[245px] h-[52px] sm:h-[58px] rounded-lg hover:opacity-90 transition-opacity">
                    Create a Property Request
                  </Button>
                </Link>

                {/* Footer disclaimer */}
                <small className="text-xs sm:text-sm font-normal mt-4 text-white/70 max-w-[500px]">
                  Your personalized property matching system backed by professional live
                  inspection support services from FindNigeriaProperties.com
                </small>

              </div>
            </div>
          </div>
          <TawkChat />
        </div>

        {footerAds[0] && (
          <div className="w-full max-w-[1280px] mx-auto px-4 pt-2 pb-12">
            <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
          </div>
        )}
      </HomeWrapper>
    </>
  );
};

export default Home;